/**
 * The steps that connect one system's CI to GAC Pro, shared by Setup and onboarding step 8
 * (V108-1-3; three-critic panel 2026-10-11: one component so the two screens can't drift).
 *
 * Everything shown comes from the server's gac-config response (secret name, facts file, workflow
 * with the signer pinned) plus a token minted in place for the "CI governance check" purpose, so the
 * user never leaves the page or has to remember a value. Focus moves to the heading when the steps
 * appear.
 */
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Button, CodeBlock } from '@aigovops/design-system';
import { createApiToken, type GacConfigResponse } from '../../lib/api';

export interface CiSetupStepsProps {
  slug: string;
  systemId: string;
  systemName: string;
  config: GacConfigResponse;
  /** Replaces the default last step ("see this system's activity"), e.g. onboarding's live wait. */
  finalStep?: React.ReactNode;
}

export function CiSetupSteps({ slug, systemId, systemName, config, finalStep }: CiSetupStepsProps) {
  const heading = useRef<HTMLHeadingElement>(null);
  const [token, setToken] = useState<string | null>(null);
  const [tokenBusy, setTokenBusy] = useState(false);
  const [tokenError, setTokenError] = useState('');

  useEffect(() => { heading.current?.focus(); }, []);

  const mintToken = async () => {
    setTokenBusy(true); setTokenError('');
    try {
      const created = await createApiToken(slug, { label: `CI governance check · ${systemName}`.slice(0, 200), purpose: 'ci_gate', system_id: systemId });
      setToken(created.plaintext);
    } catch (e) {
      setTokenError(e instanceof Error ? e.message : 'The token could not be created. Retry.');
    } finally {
      setTokenBusy(false);
    }
  };

  const tokensHref = `/w/${encodeURIComponent(slug)}/app/settings/api?purpose=ci_gate&system=${encodeURIComponent(systemId)}`;

  return (
    <div className="v75-stack" style={{ gap: 'var(--sp-4)' }}>
      <h3 ref={heading} tabIndex={-1} style={{ margin: 0 }}>Connect {systemName}'s CI</h3>
      <ol style={{ paddingLeft: 'var(--sp-6)', display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)', margin: 0 }}>
        <li>
          <strong>Add the token as a repository secret named <code>{config.secret_name}</code>.</strong>
          {token ? (
            <div style={{ marginTop: 'var(--sp-2)' }}>
              <CodeBlock label={`${config.secret_name} value`} code={token} masked copyLabel="Copy token" />
              <p className="v75-muted" style={{ margin: 'var(--sp-1) 0 0' }}>Shown once. If you lose it, create another.</p>
            </div>
          ) : (
            <div className="v75-actions" style={{ marginTop: 'var(--sp-2)' }}>
              <Button variant="secondary" disabled={tokenBusy} onClick={() => void mintToken()}>{tokenBusy ? 'Creating…' : 'Create token'}</Button>
              <Link to={tokensHref}>Manage tokens</Link>
            </div>
          )}
          {tokenError && <Alert variant="error">{tokenError}</Alert>}
        </li>
        <li>
          <strong>Save the facts file as <code>{config.facts_path}</code>.</strong>
          <p className="v75-muted" style={{ margin: 'var(--sp-1) 0 var(--sp-2)' }}>Nothing is claimed until it's true. Change a value when it is.</p>
          <CodeBlock label="Facts file" code={config.facts_json} copyLabel="Copy facts file" />
        </li>
        <li>
          <strong>Save the workflow as <code>.github/workflows/governance.yml</code> and push.</strong>
          <div style={{ marginTop: 'var(--sp-2)' }}>
            <CodeBlock label="Governance workflow" code={config.config_yaml} copyLabel="Copy workflow" />
          </div>
        </li>
        <li>
          {finalStep ?? (
            <><strong>Push and check.</strong> The first run's evidence appears on <Link to={`/w/${encodeURIComponent(slug)}/app/systems/${encodeURIComponent(systemId)}?tab=activity`}>{systemName}'s activity</Link>.</>
          )}
        </li>
      </ol>
    </div>
  );
}
